import Vue from 'vue'
import VueRouter from 'vue-router'
import { log, inspect } from '@/utils/logger'
import { currentContextForLogging } from '@/config/initGlobalMixin'
import VueGtag from 'vue-gtag'
import { onboardingRoutes } from '@/routes/onboardingRoutes'
import store from '@/store'
import { signInRoutes } from '@/routes/signInRoutes'
import { appRoutePaths, appRoutes } from '@/routes/appRoutes'
import { sharedRoutePaths, sharedRoutes } from '@/routes/sharedRoutes'
import { passwordResetRoutes } from '@/routes/passwordResetRoutes'
import { supportRoutes } from '@/routes/supportRoutes'
import { saveCurrentURLPath, setSafeAreaConfig } from '@/nativeWebInteraction/nativeWebInteraction'

Vue.use(VueRouter)

const routes = [...sharedRoutes, ...onboardingRoutes, ...signInRoutes, ...passwordResetRoutes, ...appRoutes, ...supportRoutes]

const router = new VueRouter({
    mode: 'history',
    routes,
    scrollBehavior: function (to) {
        if (to.hash) {
            return { selector: to.hash }
        } else {
            return { x: 0, y: 0 }
        }
    },
})

if (['production', 'staging', 'uat', 'test'].includes(process.env.VUE_APP_NODE_ENV as string)) {
    Vue.use(
        VueGtag,
        {
            config: { id: process.env.VUE_APP_GOOGLE_ANALYTICS_TAG },
        },
        router
    )
}

router.onError((error) => {
    log.info('router error: %{routerError}', { routerError: inspect(error) })
    // See: https://blog.francium.tech/vue-lazy-routes-loading-chunk-failed-9ee407bbd58
    if (/Loading.*chunk.*failed./i.test(error.message)) {
        log.info('Reloading page to fix stale chunk error')
        return window.location.reload()
    }

    throw error
})

router.afterEach((to) => {
    try {
        for (const member in currentContextForLogging) {
            delete currentContextForLogging[member]
        }
        saveCurrentURLPath(to.path)

        // Reset safe area to default white background on every route change
        // Since a majority of the pages are white background, it should be on the black pages to explicitly set the safe area config
        setSafeAreaConfig({ enabled: true, backgroundColor: '#FFFFFF' })
    } catch (e) {
        log.fatal('error clearing current context for logging: %{e}', { e })
    }
})

// Documentation: https://router.vuejs.org/guide/advanced/navigation-guards.html#global-before-guards
router.beforeEach((to, from, next) => {
    log.info('Aven Advisor trying to route from: %{fromPath} to: %{toPath}', { fromPath: from.path, toPath: to.path })
    window.previousPath = from.path

    log.info('Aven Advisor trying to route from: %{fromPath} to: %{toPath}', { fromPath: from.path, toPath: to.path })

    // remove any modals if needed
    document.body.classList.remove('modal-open')
    document.getElementById('modal-backdrop')?.remove()

    const hasAuthTokens = !!store.state.persistedStore.jwtTokens
    const pathRequiresAuth = to.meta?.requiresAuth
    if (pathRequiresAuth && !hasAuthTokens) {
        log.info('Aven Advisor path %{toPath} requires auth, but none found. Routing to %{rootPath}', { toPath: to.path, rootPath: sharedRoutePaths.ROOT })
        return next({ path: sharedRoutePaths.ROOT, replace: true })
    } else if (!pathRequiresAuth && hasAuthTokens) {
        log.info('Aven Advisor path %{toPath} does not requires auth, but we have auth tokens', { toPath: to.path })
        log.info('router.beforeEach: Routing to %{todayPath}', { todayPath: appRoutePaths.TODAY })
        return next({ path: appRoutePaths.TODAY, replace: true })
    }

    log.info('Aven Advisor to path (%{toPath}) and presence of auth tokens (%{hasAuthTokens}) matches. Continuing routing to requested path.', { toPath: to.path, hasAuthTokens })
    next()
})

export default router
